import React from 'react';
import { useParams, Router } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import {
	Link, Loader, BreadcrumbsText, Breadcrumbs,
	ExtendedTheme, Heading, TextBlock
} from 'neon-design-system';
import { createStyles, makeStyles } from '@material-ui/core';
import { Article, User } from '../../../shared/types';
import AdminList from '../../ui/admin/AdminList';
import { ColDef } from 'ag-grid-community';
import gql from 'graphql-tag';
import { configureLongDateFormat, formatDisplayDate } from '../../../utils/date.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';

configureLongDateFormat();

interface ArticleStatRowData {
	_id: string;
	name: string;
	email: string;
	views: number;
	lastViewed: string;
}

interface ArticleStatsBreadcrumbsProps {
	article: Article;
}

interface ArticleStatsBarProps {
	classes: any;
	article: Article;
}

interface UserDetailsCellProps {
	data: User;
	context: {
		history: any;
	};
}

export const FIND_ARTICLE_STATS_BY_ID = gql`
	query findArticleStatsById($id: ID!) {
		document: findArticleStatsById(id: $id) {
			_id
			author
			title
			html
			publishedBy { _id name type }
			contributors { _id name }
			categories { _id name }
			tags { _id name }
			state
			created
			summary
			version
			lastEdited
			publishDate
			views
			viewers {
				_id
				name
				email
				articleStats {
					articleId
					views
					lastViewed
				}
			}
		}
	}
`;

const articleStatsBarColumns = [
	{
		id: 'article-stats-status-bar-views',
		title: 'Article views',
		key: 'articleViews',
		renderCallback: ({ views = 0 }: Article) => `${views} view${views > 1 ? 's' : ''}`
	},
	{
		id: 'article-stats-status-bar-unique-viewers',
		title: 'Unique viewers',
		renderCallback: ({ viewers = [] }: Article) => `${viewers?.length} user${viewers?.length > 1 ? 's' : ''}`
	},
	{
		id: 'article-stats-status-bar-published',
		title: 'Published',
		renderCallback: (item: Article) => !item.publishDate
			? undefined
			: moment(item.publishDate).format('DD MMM YYYY HH:mm')
	},
	{
		id: 'article-stats-status-bar-last-updated',
		title: 'Last updated',
		renderCallback: (item: Article) => !item.lastEdited
			? undefined
			: moment(item.lastEdited).format('DD MMM YYYY HH:mm')
	}
]

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	container: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(6)
	},
	main: {
		flex: 1,
		flexDirection: 'column',
		maxWidth: 1200
	},
	breadCrumbsWrapper: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(4),
		'& span': {
			display: 'block'
		}
	},
	articleStatsHeadingWrapper: {
		marginBottom: theme.spacing(2)
	},
	articleStatsBar: {
		display: 'flex',
		padding: theme.spacing(2),
		background: theme.themeColors.blueGrey[800]
	},
	articleStatsBarItem: {
		marginRight: theme.spacing(4),
		'& > p:first-child': {
			fontWeight: 'bold !important'
		}
	},
	articleStatsTableCellLink: {
		fontWeight: 'bold',
		marginRight: theme.spacing(0.6)
	},
	statusBarLabel: {
		color: theme.themeColors.blueGrey[400],
		marginBottom: theme.spacing(0.25)
	}
}));

const UserDetailsCell = ({ data: user, context }: UserDetailsCellProps) => {
	const classes = useStyles();

	if (!context?.history) throw new Error('React router history must be added to ag-grid context');

	if (!user) return null;

	// TODO need get optedOutFromMarketing flag from the config, required discussion
	//	if (optedOutFromMarketing) return <>undisclosed</>;

	return (
		<Router history={context.history}>
			{
				user.name ?
					<>
						<Link
							className={classes.articleStatsTableCellLink}
							to={baseUrls.publisher.users.edit.replace(':_id', window.encodeURIComponent(user._id))}
						>
							{user.name}
						</Link>
						<span>{`<${user.email}>`}</span>
					</> :
					<div className={classes.articleStatsTableCellLink}>
						{user.email || 'Anonymous'}
					</div>
			}
		</Router>
	)
};

const ArticleStatsBreadcrumbs = ({ article }: ArticleStatsBreadcrumbsProps) => (
	<Breadcrumbs>
		<Link to={baseUrls.publisher.articles.list}>Articles</Link>
		<Link to={`${baseUrls.publisher.articles.list}/edit/${article._id}`}>{article.title}</Link>
		<BreadcrumbsText>View stats</BreadcrumbsText>
	</Breadcrumbs>
);

const ArticleStatsBar = ({ classes, article }: ArticleStatsBarProps) => (
	<div className={classes.articleStatsBar}>
		{
			articleStatsBarColumns.map((item, index) => (
				<div id={item.id} className={classes.articleStatsBarItem} key={index}>
					<TextBlock
						styleName="caption2"
						className={classes.statusBarLabel}
					>
						{item.title}
					</TextBlock>
					<TextBlock
						styleName="largeBody2"
					>
						{item.renderCallback(article)}
					</TextBlock>
				</div>
			))
		}
	</div>
);

const columnDefs: ColDef[] = [
	{
		headerName: 'User details',
		field: '_id',
		width: 800,
		cellRendererFramework: UserDetailsCell,
		menuTabs: []
	},
	{
		headerName: 'Views',
		field: 'views',
		width: 160,
		menuTabs: []
	},
	{
		headerName: 'Last visit',
		field: 'lastViewed',
		width: 224,
		cellRendererFramework: (props: { value: string }) => formatDisplayDate(props.value, false),
		menuTabs: []
	}
];

const ArticleStatsPage = (): JSX.Element => {
	const classes = useStyles();
	const { _id } = useParams<{_id: string}>();

	const { data, loading } = useQuery(FIND_ARTICLE_STATS_BY_ID, {
		fetchPolicy: 'network-only',
		variables: {
			id: _id
		}
	});

	if (loading) {
		return <Loader />;
	}

	const article = data?.document;
	const articleStatRowData: ArticleStatRowData[] = article?.viewers
		.sort(({ articleStats: a = {} as any }: User, { articleStats: b = {} as any }: User) =>
			new Date(b.lastViewed).getTime() - new Date(a.lastViewed).getTime())
		.map(({ _id, name, email, articleStats = {} as any }: Partial<User>) => {
			return {
				_id,
				name,
				email,
				views: articleStats.views,
				lastViewed: articleStats.lastViewed
			} as ArticleStatRowData
		});

	const statusBarArticleViews: number = articleStatRowData.map(c => c.views).reduce((a, b) => a + b, 0);

	return (
		<div className={classes.container}>
			<div className={classes.main}>
				<div className={classes.breadCrumbsWrapper}>
					<ArticleStatsBreadcrumbs article={article} />
				</div>
				<div id="article-stats-title" className={classes.articleStatsHeadingWrapper}>
					<Heading styleName="title1">{article.title}</Heading>
				</div>
				<ArticleStatsBar article={{ ...article, views: statusBarArticleViews }} classes={classes} />
				<AdminList
					name="users"
					showHeader={false}
					items={articleStatRowData}
					loading={loading}
					columns={columnDefs}
					showAdd={false}
				/>
			</div>
		</div>
	);
};

export default ArticleStatsPage;
